import type { DistributiveOmit } from 'react-redux';
import { z } from 'zod';
import { filtersValidator } from '@/features/filters';
import type {
  MetricColumnDataType,
  DimensionColumnDataType,
  DimensionDataFormatType,
  MetricDataFormatType,
} from '@/shared/types';
import {
  DateScale,
  Lifetime,
  serializedDateRangeValueObjectValidator,
} from '@/shared/types';

export enum ChartType {
  cohort = 'cohort',
  calendar = 'calendar',
}

interface ColumnBaseConfig {
  id: string;
  group: string;
  name: string;
  description: string;
  order: number;
}
type MetricColumn = ColumnBaseConfig & {
  type: MetricColumnDataType;
  dataType: MetricDataFormatType;
};

type DimensionColumn = ColumnBaseConfig & {
  type: DimensionColumnDataType;
  dataType: DimensionDataFormatType;
};

type AxisConfig = Record<string, MetricColumn | DimensionColumn>;

export interface ChartMeta {
  defaultAxis: {
    dimension: string;
    primaryMetric: string;
    secondaryMetric: string;
  };
  config: AxisConfig;
  dimensions: string[];
  metrics: string[];
}

export const calendarChartStateValidator = z.object({
  id: z.string(),
  type: z.literal(ChartType.calendar),
  cohortDateScale: z.nativeEnum(DateScale),
  cohortDateRange: serializedDateRangeValueObjectValidator,
  revenueDateScale: z.nativeEnum(DateScale),
  revenueDateRange: serializedDateRangeValueObjectValidator,
  probabilisticAttribution: z.boolean(),
  splitDimension: z.string(),
  primaryMetric: z.string(),
  secondaryMetric: z.string(),
  excludedSplitOptions: z.array(z.string()),
  syncedYAxes: z.boolean(),
  filters: filtersValidator,
});

export const cohortChartStateValidator = z.object({
  id: z.string(),
  type: z.literal(ChartType.cohort),
  dateScale: z.nativeEnum(DateScale),
  dateRange: serializedDateRangeValueObjectValidator,
  lifetime: z.nativeEnum(Lifetime),
  probabilisticAttribution: z.boolean(),
  splitDimension: z.string(),
  primaryMetric: z.string(),
  secondaryMetric: z.string(),
  excludedSplitOptions: z.array(z.string()),
  syncedYAxes: z.boolean(),
  filters: filtersValidator,
});

export const dataConfigValidator = cohortChartStateValidator.or(
  calendarChartStateValidator,
);

export type CohortChartState = z.infer<typeof cohortChartStateValidator>;

export type CalendarChartState = z.infer<typeof calendarChartStateValidator>;

export type Chart = CohortChartState | CalendarChartState;

export type RequestedConfig<
  T extends { excludedSplitOptions: string[]; syncedYAxes: boolean },
> = DistributiveOmit<T, 'excludedSplitOptions' | 'syncedYAxes'>;

export interface DefaultAxesParam {
  dimension: string;
  primaryMetric: string;
  secondaryMetric: string;
}

type DataPoint<T extends string> = { [key in T]: string } & {
  data: Record<string, Record<string, number>>;
};

type Timeseries<T extends string> = DataPoint<T>[];

export interface DataResponseBase<T extends string> {
  data: Timeseries<T>;
  splitOptions: Record<string, string[]>;
}
